import { formatCents, formatCount, tt } from '@agos/shared'

/**
 * The driving surface — what a driver holding a phone and a pizza bag needs on one screen.
 *
 * §9.6 retires the Expo driver app and moves drivers into the manager website. Its order of work is to port
 * the legacy web driver view (`pages/driver.tsx`, 354 lines) 1:1 first. Reading that file against the live
 * database is what produced **FB-180**, and it is why none of its wiring is carried over.
 *
 * **FB-180, in three parts.**
 *
 * 1. `driver.tsx` lists drivers with `employees.filter(e => e.role === "delivery" && e.active)`. There is no
 *    `role` column on `employees` — it is `system_role` — and no value `delivery`; the role is `driver`.
 *    Measured: **0** columns named `role`, and eleven drivers the screen therefore never shows.
 * 2. It matches orders with `o.employeeId === driverId`. On the 7 436 delivery orders `employee_id` is set
 *    on **0** and `driver_id` on **7 314** — `employee_id` is the waiter who took the order. The board was
 *    empty by construction.
 * 3. It has **no authentication**. The driver is whichever row was tapped on a public list, kept in
 *    `localStorage.driver_id`, and the delivery confirmation is posted with no Authorization header.
 *
 * So the surface has no driver picker. The signed-in employee is the driver, `GET /employees/my-deliveries`
 * is scoped to them by the server, and every write goes through the authenticated client.
 *
 * **And the wallet is a career, not a day.** `my-wallet` answers `walletTotal: 40 045,35 €` over 1 841
 * orders — everything one driver has ever delivered. Legacy's strip shows *today*. Printing the lifetime
 * figure where a driver reads *what I am carrying* is FB-101's mistake with a bigger number, so the two are
 * labelled apart and only the cash half is ever called money that gets handed over.
 */

// ─── The orders a driver is carrying ─────────────────────────────────────────

export interface DeliveryItem {
  productName: string
  quantity: number
}

export interface Delivery {
  id: number
  orderNumber: string
  status: string
  totalCents: number
  paymentMethod: string
  customerName: string | null
  customerPhone: string | null
  customerAddress: string | null
  createdAt: string | null
  notes: string | null
  items: DeliveryItem[]
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : 0
}

function euros(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value * 100)
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0
  }
  return 0
}

export function deliveriesOf(payload: unknown): Delivery[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    const items = Array.isArray(row.items) ? row.items : []
    return [
      {
        id: row.id,
        orderNumber: text(row.orderNumber) ?? String(row.id),
        status: text(row.status) ?? '',
        totalCents: euros(row.total),
        paymentMethod: text(row.paymentMethod) ?? '',
        customerName: text(row.customerName),
        customerPhone: text(row.customerPhone),
        customerAddress: text(row.customerAddress),
        createdAt: text(row.createdAt),
        notes: text(row.notes),
        items: items.map((item) => {
          const line = item as Record<string, unknown>
          return { productName: text(line.productName) ?? '', quantity: count(line.quantity) }
        }),
      },
    ]
  })
}

/**
 * Whether the customer pays the driver at the door.
 *
 * `cash` and `card_courier` are money the driver handles; a terminal or an online payment is already
 * settled. A driver needs to know which one is walking up the path with them.
 */
export function isPaidToDriver(paymentMethod: string): boolean {
  return paymentMethod === 'cash' || paymentMethod === 'card_courier'
}

/*
 * `paymentLabel` was here and is gone with §9.384, FB-683.
 *
 * It named the same six methods as the dispatch board, in different words and with no colour, and
 * the owner asked for the driver's badge to *"match colors from Rozvoz nástenka"*. That is one
 * vocabulary, not two: `paymentMethodBadge` moved into `@agos/shared` and the card reads it. Left as
 * a note rather than silently deleted, because the next person to want a payment word here should
 * find the shared one instead of writing a third.
 */

/** What the driver has to collect at this door, or nothing because it is already paid. */
export function collectCents(delivery: Delivery): number | null {
  return isPaidToDriver(delivery.paymentMethod) ? delivery.totalCents : null
}

export function collectLabel(delivery: Delivery): string {
  const cents = collectCents(delivery)
  return cents === null ? tt('Nič sa nevyberá - zaplatené') : `${tt('Vyberte')} ${formatCents(cents)}`
}

export function statusLabel(status: string): string {
  if (status === 'on_the_way') return tt('Na ceste')
  if (status === 'at_address') return tt('Na adrese')
  if (status === 'ready') return tt('Pripravená na vyzdvihnutie')
  if (status === 'delivered') return tt('Doručená')
  return status
}

/** How long the order has been waiting, in the bands §14.8 uses everywhere else. */
export type Urgency = 'fresh' | 'watch' | 'late'

export function minutesWaiting(delivery: Delivery, now: number): number {
  if (delivery.createdAt === null) return 0
  const stamp = Date.parse(delivery.createdAt)
  if (Number.isNaN(stamp) || stamp > now) return 0
  return Math.floor((now - stamp) / 60_000)
}

export function urgencyOf(minutes: number): Urgency {
  if (minutes >= 60) return 'late'
  if (minutes >= 30) return 'watch'
  return 'fresh'
}

export function elapsedLabel(minutes: number): string {
  if (minutes < 60) return `${formatCount(minutes)} min`
  return `${formatCount(Math.floor(minutes / 60))} h ${formatCount(minutes % 60)} min`
}

/** Oldest first: the one that has been waiting longest is the one to worry about. */
export function orderedDeliveries(deliveries: readonly Delivery[]): Delivery[] {
  return [...deliveries].sort((left, right) => (left.createdAt ?? '').localeCompare(right.createdAt ?? ''))
}

/** What is on the driver's phone right now, from the deliveries themselves. */
export function runTotals(deliveries: readonly Delivery[]): {
  stops: number
  collectCents: number
  alreadyPaid: number
} {
  const collecting = deliveries.filter((delivery) => isPaidToDriver(delivery.paymentMethod))
  return {
    stops: deliveries.length,
    // A plain integer reduce: `sumCents` parses its inputs and would multiply cents by a hundred.
    collectCents: collecting.reduce((total, delivery) => total + delivery.totalCents, 0),
    alreadyPaid: deliveries.length - collecting.length,
  }
}

// ─── Navigation and the phone ────────────────────────────────────────────────

/**
 * Where *navigate* goes.
 *
 * The RN app opens `google.navigation:q=…`, an **Android intent** with no web equivalent. The universal
 * Maps directions URL works on every platform and is what the app itself falls back to, so it is the only
 * one here — a link that does nothing on an iPhone is worse than one that opens a map.
 */
export function navigationUrl(address: string | null): string | null {
  const trimmed = (address ?? '').trim()
  if (trimmed === '') return null
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(trimmed)}`
}

export function telUrl(phone: string | null): string | null {
  const trimmed = (phone ?? '').replace(/\s/g, '')
  if (trimmed === '') return null
  return `tel:${trimmed}`
}

/**
 * A short buzz to confirm a tap, where the browser has one.
 *
 * The RN app calls `expo-haptics` sixty-one times. `navigator.vibrate` is the web's equivalent and **iOS
 * Safari does not implement it at all** — so this is best-effort by design, and every action it accompanies
 * also changes something visible. A driver on an iPhone must not be relying on a buzz that never comes.
 */
export function buzz(navigatorLike: { vibrate?: (pattern: number | number[]) => boolean }): boolean {
  if (typeof navigatorLike.vibrate !== 'function') return false
  try {
    return navigatorLike.vibrate(15)
  } catch {
    return false
  }
}

// ─── The wallet ──────────────────────────────────────────────────────────────

export interface Wallet {
  name: string
  isAvailable: boolean
  vehicleId: number | null
  vehicleName: string | null
  lifetimeOrders: number
  lifetimeCashCents: number
  lifetimeCardCents: number
  lifetimeTotalCents: number
}

export function walletOf(payload: unknown): Wallet | null {
  if (payload === null || payload === undefined || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  const vehicle = (row.vehicle ?? {}) as Record<string, unknown>
  return {
    name: text(row.name) ?? '',
    isAvailable: row.isAvailable === true,
    vehicleId: typeof row.vehicleId === 'number' ? row.vehicleId : null,
    vehicleName: text(vehicle.name),
    lifetimeOrders: count(row.orderCount),
    lifetimeCashCents: euros(row.cashTotal),
    lifetimeCardCents: euros(row.cardTotal),
    lifetimeTotalCents: euros(row.walletTotal),
  }
}

/**
 * What the wallet figures actually are.
 *
 * `walletTotal` is every order this driver has ever delivered — 40 045,35 € over 1 841 orders for one of
 * them. Only the cash half was ever in anybody's pocket, and none of it is owed today. Legacy's strip shows
 * a day; this endpoint has no window at all, so the surface says which is which rather than letting a
 * lifetime figure sit under a heading that reads like a debt.
 */
export function walletNote(wallet: Wallet): string {
  return `${tt('Za celý čas')}: ${formatCount(wallet.lifetimeOrders)} ${tt('rozvozov')}, ${tt('z toho v hotovosti')} ${formatCents(wallet.lifetimeCashCents)}. ${tt('Nie je to suma, ktorú máte teraz pri sebe.')}`
}

export function shiftLabel(wallet: Wallet): string {
  return wallet.isAvailable ? tt('Na smene') : tt('Mimo smeny')
}

/**
 * Why the board is empty, which is not the same as *you have nothing to do*.
 *
 * `my-deliveries` is `status IN ('on_the_way', 'at_address')`, so it holds an order only while it is out.
 * Assignment happens on `/dispatch` and the order arrives here already moving; there is no *ready to pick
 * up* list, because in the whole dataset **no order has ever been `ready` with a driver on it**.
 */
export function emptyBoardNote(wallet: Wallet | null): string {
  if (wallet !== null && !wallet.isAvailable) {
    return tt('Ste mimo smeny, takže vám dispečer nemôže nič prideliť. Prepnite sa na smenu vyššie.')
  }
  return tt('Zatiaľ nič. Objednávka sa tu objaví, keď vám ju dispečer pridelí a vy vyrazíte.')
}

// ─── The vehicle a driver is on ──────────────────────────────────────────────

export interface Vehicle {
  id: number
  name: string
  licensePlate: string | null
}

export function vehiclesOf(payload: unknown): Vehicle[] {
  const rows = Array.isArray(payload) ? payload : ((payload as { data?: unknown })?.data ?? [])
  if (!Array.isArray(rows)) return []
  return rows.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    return [
      {
        id: row.id,
        name: text(row.name)?.trim() ?? String(row.id),
        licensePlate: text(row.licensePlate)?.trim() ?? null,
      },
    ]
  })
}

export function vehicleLabel(vehicle: Vehicle): string {
  return vehicle.licensePlate === null ? vehicle.name : `${vehicle.name} · ${vehicle.licensePlate}`
}

/**
 * Why the vehicle matters, said where a driver picks one.
 *
 * `/trip-log` and `/delivery-statistics` attribute distance and fuel by vehicle, so a driver left on last
 * week's van puts this week's diesel on it. The figure nobody checks is the one that quietly drifts.
 */
export function vehicleNote(current: Vehicle | null): string {
  if (current === null) {
    return tt('Nemáte vybrané vozidlo - kilometre a nafta sa nemajú k čomu priradiť.')
  }
  return `${tt('Jazdíte na')} ${vehicleLabel(current)}. ${tt('Podľa toho sa priradia kilometre aj spotreba, tak to prepnite, keď si vymeníte auto.')}`
}

export function currentVehicle(wallet: Wallet | null, vehicles: readonly Vehicle[]): Vehicle | null {
  if (wallet?.vehicleId == null) return null
  return vehicles.find((vehicle) => vehicle.id === wallet.vehicleId) ?? null
}

/**
 * Whether this order can be handed back - §9.370, FB-658.
 *
 * **The server's own condition, spelled the same way.** `OrderStatusService.IN_THE_FIELD` is
 * `{on_the_way, at_address}` and it is what lets a **driver** move an order to `quality_check` - a
 * transition otherwise reserved for `main_admin`, `admin` and `cook`. Anywhere else the server
 * refuses, so the button is **absent** rather than offered and refused: a driver at somebody's door
 * should not be reading a permission error.
 *
 * Restated here rather than inferred from *anything not delivered*, because that would offer it on
 * an order still at the counter, which the server would refuse.
 */
export function mayHandBack(status: string | undefined): boolean {
  return status === 'on_the_way' || status === 'at_address'
}

/**
 * What the status log will read months later - §9.370.
 *
 * Through `tt` like every other sentence: the reason is `@NotBlank` on the server and ends up on a
 * screen somebody reads, so it is a translated string rather than a constant with diacritics in it.
 */
export function handBackReason(): string {
  return tt('Zákazník neprevzal')
}

export function handBackNote(orderNumber: string | undefined): string {
  return `#${orderNumber ?? ''} · ${tt('Kuchyňa a dispečer to uvidia. Jedlo zostáva u vás, kým nepovedia inak.')}`
}

